/**
 * Gallery axis strategy for 'run' mode.
 *
 * Displays one row per run, with step and index controlled via sliders.
 */

import {resolveCurrentStep} from '../../utils/stepUtils';
import {type GridRow, type SlotRegistry, type TransformInput} from '../types';
import {createCellsForMediaKeys} from '../utils/cells';
import {
  caption,
  compareEntry,
  indexSlider,
  label,
  stepSlider,
} from '../utils/slots';
import {createEmptySlotRegistry} from '../utils/slotUtils';
import {type GalleryAxisStrategy} from './GalleryAxisStrategy';

export class RunAxisStrategy implements GalleryAxisStrategy {
  getAxisValues(input: TransformInput, maxItems?: number): number[] {
    const {runs} = input;
    const runsToProcess = maxItems
      ? Math.min(runs.length, maxItems)
      : runs.length;

    // Return run indices
    const axisValues: number[] = [];
    for (let i = 0; i < runsToProcess; i++) {
      axisValues.push(i);
    }

    return axisValues;
  }

  createRows(input: TransformInput, axisValues: number[]): GridRow[] {
    const {runs, stepMeta, config, allMasks, mediaType} = input;
    const {mediaKeys} = config;
    const currentStep = resolveCurrentStep(
      config.stepIndex,
      stepMeta,
      config.mediaSliderKey
    );
    const currentIndex = config.mediaIndex ?? 0;

    if (!mediaKeys || mediaKeys.length === 0 || !mediaType) {
      return [];
    }

    const rows: GridRow[] = [];

    for (const runIndex of axisValues) {
      const run = runs[runIndex];
      const runStepMeta = stepMeta.runs.find(r => r.id === run.name);
      if (!runStepMeta) {
        continue;
      }

      const cells = createCellsForMediaKeys(
        run,
        runStepMeta,
        currentStep,
        currentIndex,
        mediaKeys,
        config,
        allMasks,
        mediaType
      );

      if (cells.length > 0) {
        rows.push({
          cells,
          rowKey: `run-${runIndex}`,
        });
      }
    }

    return rows;
  }

  buildSlotRegistry(
    stepMeta?: TransformInput['stepMeta'],
    isSingleRunView?: boolean
  ): SlotRegistry {
    // Run axis: runs shown on each tile, step and index are controlled
    const slots = createEmptySlotRegistry();
    // When viewing a single run, omit redundant header
    if (!isSingleRunView) {
      slots.tileHeader = [label('run', 'value', 'text-[10px]')];
    }
    slots.tileFooter = [caption()];

    const hasStepSlider = stepMeta?.steps && stepMeta?.steps.length > 1;
    if (hasStepSlider) {
      slots.contentFooter!.push(stepSlider());
    }

    // Only add index slider if there are multiple indexes
    const maxIndex = stepMeta?.maxIndex ?? 0;
    const hasIndexSlider = maxIndex > 0;
    if (hasIndexSlider) {
      slots.contentFooter!.push(indexSlider());
    }

    slots.contentHeader = [
      {
        control: 'pagination',
        type: 'component',
      },
    ];

    return slots;
  }
}
